import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from '@/components/ui/use-toast';

export interface CollectionSchedule {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  schedule_type: 'recurring' | 'one-time' | 'conditional';
  frequency: string;
  workflow_id?: string;
  trigger_conditions: any;
  notification_settings: any;
  is_active: boolean;
  last_run_at?: string;
  next_run_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateCollectionScheduleData {
  name: string;
  description?: string;
  schedule_type: 'recurring' | 'one-time' | 'conditional';
  frequency: string;
  workflow_id?: string;
  trigger_conditions?: any;
  notification_settings?: any;
  is_active?: boolean;
}

export interface UpdateCollectionScheduleData extends Partial<CreateCollectionScheduleData> {
  id: string;
}

export const useCollectionSchedules = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const schedulesQuery = useQuery({
    queryKey: ['collection-schedules', user?.id],
    queryFn: async () => {
      // Note: This will fail until the types are updated after migration
      // For now, we'll return an empty array
      try {
        const { data, error } = await (supabase as any)
          .from('collection_schedules')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        return data as CollectionSchedule[];
      } catch (error) {
        console.warn('Collection schedules table not yet available:', error);
        return [];
      }
    },
    enabled: !!user?.id,
  });

  const createScheduleMutation = useMutation({
    mutationFn: async (scheduleData: CreateCollectionScheduleData) => {
      const { data, error } = await (supabase as any)
        .from('collection_schedules')
        .insert([{
          ...scheduleData,
          user_id: user?.id,
          trigger_conditions: scheduleData.trigger_conditions || {},
          notification_settings: scheduleData.notification_settings || {},
          is_active: scheduleData.is_active ?? true,
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-schedules'] });
      toast({
        title: "Schedule created",
        description: "Collection schedule has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to create schedule",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateScheduleMutation = useMutation({
    mutationFn: async ({ id, ...scheduleData }: UpdateCollectionScheduleData) => {
      const { data, error } = await (supabase as any)
        .from('collection_schedules')
        .update(scheduleData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-schedules'] });
      toast({
        title: "Schedule updated",
        description: "Collection schedule has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update schedule",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteScheduleMutation = useMutation({
    mutationFn: async (scheduleId: string) => {
      const { error } = await (supabase as any)
        .from('collection_schedules')
        .delete()
        .eq('id', scheduleId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collection-schedules'] });
      toast({
        title: "Schedule deleted",
        description: "Collection schedule has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to delete schedule",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    schedules: schedulesQuery.data || [],
    isLoading: schedulesQuery.isLoading,
    error: schedulesQuery.error,
    createSchedule: createScheduleMutation.mutate,
    updateSchedule: updateScheduleMutation.mutate,
    deleteSchedule: deleteScheduleMutation.mutate,
    isCreating: createScheduleMutation.isPending,
    isUpdating: updateScheduleMutation.isPending,
    isDeleting: deleteScheduleMutation.isPending,
  };
};